import * as THREE from 'three'
import { AsianArchitecture } from './AsianArchitecture'
import { asianCities,asianRentIds } from './asianWorlds'
import { neighborhoodKind,neighborhoodSeed } from './neighborhoodThemes'
import { munichBuildingTypes,type MunichBuildingType } from './munichNeighborhood'
const cream='#e5d9bf'

/** The same sixteen parcel roles, built with local volumes and construction details. */
export class AsianNeighborhood extends AsianArchitecture {
  readonly usedTypes=new Set<MunichBuildingType>()
  readonly parcels:{x:number;z:number;radius:number}[]=[]
  private shop(label:string,x=0,z=2.23,w=4.1){
    const p=asianCities[this.city];this.box(x,1.06,z,w,1.32,.1,p.wood)
    for(const side of [-1,1])this.eastWindow(x+side*w*.27,1.1,z+.08,w*.29,1.02)
    this.sign(label,x,1.97,z+.14,w,.34,'#ecd6a0');this.box(x,1.7,z+.26,w,.09,.43,p.accent)
    if(this.city==='tokyo')for(let dx=-w*.42;dx<w*.43;dx+=.4)this.box(x+dx,1.48,z+.46,.32,.36,.04,p.accent)
    for(const side of [-1,1])this.lantern(x+side*w*.43,1.5,z+.4,.13)
  }
  private screen(x:number,y:number,z:number,w:number,h:number,text:string){
    this.box(x,y,z,w+.1,h+.1,.16,this.city==='tokyo'?'#be816c':'#729d9d');this.sign(text,x,y,z+.1,w,h,'#d9e0c7')
  }
  private model(kind:MunichBuildingType,seed:number,level=0,label?:string){
    this.usedTypes.add(kind)
    const p=asianCities[this.city],city=this.city,extra=seed%3*.2+Math.min(level,5)*.24,index=munichBuildingTypes.indexOf(kind)
    const names=city==='beijing'?['茶馆','街角公寓','四合院','市集','画廊','会馆','工坊','商务中心','客栈','影院','商业街','温室花园','钟楼','花园住宅','园亭','艺术空间']:city==='seoul'?['찻집','코너빌딩','한옥마당','시장','갤러리','문화관','공방','오피스','게스트하우스','영화관','상가','온실','시계탑','테라스','정자','예술공간']:['商店','角のビル','中庭','市場','ギャラリー','会館','工房','オフィス','旅館','映画館','商店街','温室','時計台','テラス','東屋','アトリエ']
    const title=label??names[index]
    this.box(0,.2,0,5.6,.18,5.8,'#c7c3b4')
    if(kind==='merchant'){
      // Paired narrow shophouses; tile awnings and timber screens replace European gables.
      for(const s of [-1,1]){const h=3.7+extra+(s>0?.7:0);this.urbanBlock(s*1.18,-.15,2.12,3.9,h,seed+(s>0?1:0));this.eastRoof(s*1.18,h+.4,-.15,2.3,4.1,.85);this.shop(s<0?title:names[0],s*1.18,1.88,1.94)}
    }else if(kind==='corner'){
      const h=7.2+extra;this.urbanBlock(-.35,-.35,3.65,3.65,h,seed,true)
      this.disc(1.18,h/2+.32,1.18,1.13,h,this.facade('teal'))
      for(let y=1;y<h;y+=1.05)this.disc(1.18,y,1.18,1.17,.11,'#b7c4bd')
      this.shop(title,-.36,1.55,3.45);this.box(-1.75,h*.6,1.58,.45,3,.2,p.accent);this.screen(-1.75,h*.6,1.72,.32,2.6,names[1])
    }else if(kind==='courtyard'){
      const h=2.1+extra*.4
      this.hall(0,-1.42,4.55,1.35,h);for(const s of [-1,1])this.at(s*1.7,.15,1,Math.PI/2,()=>this.hall(0,0,3.8,1.1,h))
      this.box(0,.33,.2,2.1,.05,2.2,'#e4dbc4');this.box(0,.36,.2,.55,.03,2.3,'#aaa38f');this.tree(.58,.15,.4,city!=='beijing');this.sign(title,0,1.15,2.37,1.6,.32);this.eastRoof(0,2,2,1.8,.9,.6)
    }else if(kind==='market'){
      this.box(0,.85,0,4.55,1.05,4.4,p.walls[seed%5]);for(const s of [-1,1])this.hall(s*1.1,-.15,1.9,3.8,2+extra*.3)
      this.shop(title,0,2.27,4.25);for(let i=0;i<8;i++){this.box(-1.7+i*.48,.62,2.56,.36,.35,.24,'#a28b66');this.ball(-1.7+i*.48,.89,2.56,.15,i%2?'#8f9d68':'#d29269')}
    }else if(kind==='gallery'){
      this.urbanBlock(-.65,-.45,3.1,3.7,4.7+extra,seed)
      this.box(1.6,1.95,.2,1.45,3.2,4.15,'#c0785b');for(let y=1;y<3.4;y+=.42)this.box(1.6,y,2.3,1.4,.09,.12,'#dfb789')
      this.shop(title,-.65,1.45,2.95);this.screen(-.7,3.35,1.48,2.1,1.1,names[4]);this.ball(1.55,.9,2.23,.35,'#608c8f')
    }else if(kind==='mansion'){
      this.hall(0,-.4,4.3,3.65,3.1+extra*.5,true)
      this.hall(0,-.65,3.1,2.65,4.7+extra*.5,true);this.steps(0,2.48,2.3,3);this.sign(title,0,2.84,1.51,2.6,.32)
    }else if(kind==='workshop'){
      this.urbanBlock(0,-.1,4.4,4.4,3.2+extra,seed)
      for(const x of [-1.5,0,1.5]){this.box(x,3.62+extra,0,1.25,.7,4.2,'#739494');this.eastRoof(x,3.8+extra,0,1.45,4.5,.65,'#6c7778')}
      this.shop(title);for(const x of [-1.7,1.7])this.box(x,.63,2.55,.54,.6,.35,'#a98e6a')
    }else if(kind==='modern'){
      this.urbanBlock(0,0,4.6,4.45,2.3,seed,true)
      this.urbanBlock(-.45,-.4,3.55,3.4,12+extra,seed,true)
      for(const x of [-1.82,-.9,0,.9])this.box(x,6.4,1.35,.055,11.5,.09,'#bfcdc6')
      this.shop(title);this.box(.6,13+extra,-.8,.06,1.3,.06,'#849e9c')
    }else if(kind==='inn'){
      const h=4.5+extra;this.urbanBlock(0,-.25,4.3,4.15,h,seed);this.eastRoof(0,h+.42,-.25,4.7,4.5,1.1)
      for(let x=-1.8;x<2;x+=.32)this.box(x,3.5,1.91,.07,1.6,.13,p.wood)
      this.shop(title,0,1.98);this.eastRoof(0,2.1,2.2,2.2,1.1,.56)
    }else if(kind==='cinema'){
      this.urbanBlock(-.2,-.25,4.3,4.2,6.1+extra,seed)
      this.screen(-.45,4.4,1.94,3.1,2.1,names[9]);this.shop(title,0,2.05)
      for(let i=0;i<4;i++)this.screen(1.9,3+i*.88,2.09,.53,.73,[...names[9]][i%[...names[9]].length])
      this.box(0,2.36,2.44,4.3,.2,.63,p.accent)
    }else if(kind==='arcade'){
      for(const s of [-1,1])this.urbanBlock(s*1.68,-.2,1.45,4.2,3.6+extra,seed)
      this.box(0,3.5+extra,-.1,2.08,.13,4.4,this.mat('#a6c3bb',.3));for(let z=-2;z<2.3;z+=.55)this.box(0,3.6+extra,z,2.2,.07,.045,cream)
      this.sign(title,0,2.96,2.33,4.2,.44);for(const s of [-1,1])this.lantern(s*.86,2.7,2.2,.18)
    }else if(kind==='conservatory'){
      this.box(0,1.67,0,4.3,2.7,4.4,this.mat('#7ea8a4',.2));this.eastRoof(0,3.05,0,4.6,4.7,1.25,'#87a8a6')
      for(const s of [-1,1])for(let x=-2;x<=2;x+=.48){this.box(x,1.75,s*2.24,.055,2.9,.06,cream);this.box(s*2.18,1.75,x,.06,2.9,.055,cream)}
      this.sign(title,0,1.66,2.35,3.3,.32);for(const x of [-1.7,1.7])this.tree(x,2.36,.3)
    }else if(kind==='clockhouse'){
      this.urbanBlock(0,0,4.3,4.2,4.2+extra,seed)
      this.urbanBlock(.1,-.4,2,2,8+extra,seed);this.eastRoof(.1,8.35+extra,-.4,2.5,2.5,.95)
      const q=new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1,0,0),Math.PI/2)
      this.add(this.cylinder,this.mat('#e7d9b5'),.1,7.3+extra,.66,.65,.09,.65,0,q);this.box(.1,7.5+extra,.73,.04,.42,.04,p.wood);this.box(.29,7.3+extra,.73,.42,.04,.04,p.wood);this.shop(title)
    }else if(kind==='terraces'){
      for(let tier=0;tier<3;tier++){const z=-tier*.45,d=4.35-tier*.85,y=tier*1.8
        this.box(0,y+1.25,z,4.4,1.8,d,p.walls[(seed+tier)%5]);for(const x of [-1.5,0,1.5])this.eastWindow(x,y+1.3,z+d/2+.04,.9,1)
        this.box(0,y+2.21,z+d/2,4.5,.1,.65,cream);for(let x=-2;x<=2;x+=.24)this.box(x,y+2.46,z+d/2+.3,.025,.45,.035,p.wood);for(const x of [-1.7,1.7])this.ball(x,y+2.47,z+d/2,.24,'#719273')}
      this.shop(title,0,2.32)
    }else if(kind==='rotunda'){
      this.disc(0,.48,0,2.2,.32,cream)
      for(let i=0;i<8;i++){const a=i*Math.PI/4;this.disc(Math.cos(a)*1.8,1.9,Math.sin(a)*1.8,.1,2.7,p.wood)}
      this.add(this.roundRoof,this.mat(p.roof),0,3.25,0,2.6,1.6,2.6);this.disc(0,5,0,.06,.4,'#c7ab68');this.sign(title,0,2.37,1.85,2.5,.3)
    }else{
      this.urbanBlock(0,-.15,4.4,4.35,6.2+extra,seed)
      for(const side of [-1,1])this.box(side*1.25,3.55,2.08,.12,5.5,.17,p.wood)
      for(let y=2.8;y<6;y+=1.4)this.box(0,y,2.17,4.4,.13,.36,'#84908a')
      this.shop(title);this.screen(0,5,2.2,1.4,1.4,names[15])
    }
  }
  place(x:number,z:number,scale:number,rotation:number,index:number){const radius=2.97*scale;if(this.parcels.some(p=>Math.abs(x-p.x)<radius+p.radius&&Math.abs(z-p.z)<radius+p.radius))return;this.parcels.push({x,z,radius});this.at(x,z,scale,rotation,()=>this.model(neighborhoodKind(index),neighborhoodSeed(x,z)),true)}
  property(group:THREE.Group,tileId:number,level:number,label:string){this.model(neighborhoodKind(Math.max(0,asianRentIds.indexOf(tileId))),tileId,level,label);this.flush(group)}
  finish(){this.flush(this.root)}
}
